import React from "react";
import { connect } from "react-redux";
import errorSvg from "assets/img/error-red.svg";
import loadingSvg from "assets/img/loading.svg";
import successSvg from "assets/img/success.svg";
import { Dispatch } from "interfaces/dispatch";
import { State } from "state";
import {
  FileUploadState,
  getSASURLUpload,
  updateUploadedFiles,
} from "state/create_file_upload";
import { fetchProject, updateProject } from "state/project";
import { AuthUser } from "state/auth_user";
import { Grid, Typography } from "@material-ui/core";
import { FolderIcon } from "lucide-react";
import axios from "axios";

// Redux actions
import {
  setUploadProgress,
  setLoading,
  setError,
  clearUploads,
  removeFile,
  setUploadProject,
} from "state/uploading-state";
import { fetchAllProjectFiles } from "state/files";
import {getItem} from "../../helpers/storage";
import {IMPERSONATIONUSER} from "../../state/auth";
import Constants from "../../helpers/constants";

interface FileUploadOwnProps {
  label: string;
  url: string;
  prefix: string;
  projectId?: string | number;
  fileStatus?: string;
  basicImport?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  accept?: string;
  dataQa?: string;
  user?: AuthUser;
  enableCsvDownload?: boolean;
  project?: any;
  chain: string;
}

interface FileUploadCallbackProps {
  upload: FileUploadState;
  afterUploadFunction?: (arg?: any) => void | Promise<any>;
  beforeUploadFunction?: (...args: any[]) => Promise<any>;
}

interface FileUploadActions {
  fetchProject: (id: string | number) => void;
  getSASURLUpload: (
    prefix: string,
    projectId: number,
    chain: string,
    fileStatus?: string,
    fileNames?: string[],
    userId?: number
  ) => Promise<any>;
  updateUploadedFiles: (
    projectId: number | string,
    fileStatus?: string,
    filename?: string,
    chainId?: number
  ) => Promise<void>;
  setUploadProgress: (fileName: string, percent: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (hasError: boolean) => void;
  clearUploads: () => void;
  removeFile: (fileName: string) => void;
  setUploadProject: (project: any) => void;
  updateProject: (id: string | number, fields: any) => Promise<void>;
  fetchAllProjectFiles: (projectId: number) => void;
}

interface FileUploadStateProps {
  uploadingState: {
    uploadedFiles: string[];
    uploadProgress: Record<string, number>;
    loading: boolean;
    hasError: boolean;
    project?: any;
  };
  project?: any;
}

interface FileUploadInternalState {
  active: boolean;
  success: boolean;
  originalPublishedValue?: boolean | number;
}

type FileUploadProps = FileUploadOwnProps &
  FileUploadCallbackProps &
  FileUploadActions &
  FileUploadStateProps;

// Helper to upload a chunk
const uploadChunk = async (
  chunk: Blob,
  sasUrl: string,
  blockId: string,
  start: number,
  file: File,
  blobAxios: any,
  updateProgress: (name: string, percent: number) => void
) => {
  const arrayBuffer = await chunk.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);

  const blockUrl = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;

  await blobAxios.put(blockUrl, uint8Array, {
    onUploadProgress: (e: any) => {
      const percent = Math.round(((start + e.loaded) / file.size) * 100);
      updateProgress(file.name, Math.min(100, percent));
    },
  });
};

// Main Component
class ProjectCommonFileUpload extends React.Component<
  FileUploadProps,
  FileUploadInternalState
> {
  public state: FileUploadInternalState = {
    active: false,
    success: false,
    originalPublishedValue: undefined,
  };
  public timer: number = -1;
  fileInputRef: HTMLInputElement | null | undefined;

  // Update file progress
  updateProgress = (fileName: string, percent: number) => {
    this.props.setUploadProgress(fileName, percent);
  };

  componentDidMount() {
    if (this.props.project) {
      this.props.setUploadProject(this.props.project);
    }
  }
  componentDidUpdate(prevProps: FileUploadProps) {
    if (prevProps.project !== this.props.project && this.props.project) {
      this.props.setUploadProject(this.props.project);
    }
    const { upload: prevUpload } = prevProps;
    const { upload, clearUploads } = this.props;

    if (prevUpload.loading && !upload.loading && !upload.hasError && upload.content) {
      this.setState({ success: true }, () => {
        this.timer = window.setTimeout(() => {
          this.setState({ success: false, active: false });
          clearUploads();
        }, 5000);
      });
    }
  }

  componentWillUnmount(): void {
    clearTimeout(this.timer);
  }

  // Handle file upload
  handleUpload(
    prefix: string,
    files: File[],
    chain: string,
    projectId?: string | number,
    fileStatus?: string,
    fileNames?: string[],
    userId?: number
  ) {
    if (!projectId) return;

    this.props.setLoading(true);
    this.props.setError(false);

    // Store the original published value before modifying it
    const originalPublished = this.props.project?.published;
    this.setState({ originalPublishedValue: originalPublished });

    // Determine the upload status based on current published value
    // If published is 0 (unpubslished) or false, set to 2 (Unpublished-File Processing) during upload
    // If published is 1 (Published) or true, set to 3 (Published-File Processing)during upload
    let uploadStatus;
    if (originalPublished === 0 || originalPublished === false || !originalPublished) {
      uploadStatus = 2;
    } else if (originalPublished === 1 || originalPublished === true) {
      uploadStatus = 3;
    } else {
      uploadStatus = originalPublished;
    }

    // Update published status while preserving all other fields
    if (this.props.updateProject && this.props.project) {
      const currentProject = this.props.project;
      this.props.updateProject(String(projectId), {
        published: uploadStatus,
        // Preserve all existing fields
        chain: currentProject.chain?.id,
        name: currentProject.name,
        season_id: currentProject.season_id,
        year: currentProject.project_year,
        projectType: currentProject.project_type === "Validation" ? 1 : currentProject.project_type === "File Sharing" ? 2 : undefined,
        validation_deadline: currentProject.validation_deadline,
        commentType: currentProject.commentTypes?.map((ct: any) => ct.id),
        commentsAllowed: currentProject.commentsAllowed,
        timezone_id: currentProject.timezone_id,
        est_complete_date: currentProject.est_complete_date,
        expires_at: currentProject.expires_at,
        automatedAnnouncement: currentProject.automatedAnnouncement,
        automatedDataProcessing:currentProject.automatedDataProcessing,
        surveyLink: currentProject.survey_url,
        surveyTitle: currentProject.surveyTitle,
      }).catch((err) => {
        console.error('Error updating project before upload:', err);
      });
    }

    files.forEach((f) => this.props.setUploadProgress(f.name, 0));

    this.props
      .getSASURLUpload(prefix, Number(projectId), chain, fileStatus, fileNames, userId)
      .then(async (result: any) => {
        if (result && Array.isArray(result["sasURLs"]) && result["sasURLs"].length > 0) {
          for (let i = 0; i < result["sasURLs"].length; i++) {
            const sasFile = result["sasURLs"][i];
            const file = files[i];
            if (!file) continue;

            const blobAxios = axios.create();
            if (
              blobAxios.defaults.headers &&
              blobAxios.defaults.headers.common &&
              blobAxios.defaults.headers.common["Authorization"]
            ) {
              delete blobAxios.defaults.headers.common["Authorization"];
            }

            try {
              await blobAxios.put(sasFile["url"], "", { headers: { "x-ms-blob-type": "BlockBlob" } });
            } catch (err) { }

            const chunkSize = 8 * 1024 * 1024;
            const blockIds: string[] = [];
            const CONCURRENCY = 5;
            const tasks = [];

            for (let start = 0; start < file.size; start += chunkSize) {
              const chunk = file.slice(start, start + chunkSize);
              const blockNumber = blockIds.length + 1;
              const blockId = btoa(`block-${String(blockNumber).padStart(6, "0")}`);
              blockIds.push(blockId);
              tasks.push(() =>
                uploadChunk(chunk, sasFile["url"], blockId, start, file, blobAxios, this.updateProgress)
              );
              // await uploadChunk(chunk, sasFile["url"], blockId, start, file, blobAxios, this.updateProgress);
            }
            for (let i = 0; i < tasks.length; i += CONCURRENCY) {
              const batch = tasks.slice(i, i + CONCURRENCY);
              await Promise.all(batch.map((fn) => fn()));
            }

            const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds
              .map((id) => `<Latest>${id}</Latest>`)
              .join("")}</BlockList>`;

            const commitUrl = new URL(sasFile["url"]);
            commitUrl.searchParams.append("comp", "blocklist");

            if (
              blobAxios.defaults.headers &&
              blobAxios.defaults.headers.common &&
              blobAxios.defaults.headers.common["Authorization"]
            ) {
              delete blobAxios.defaults.headers.common["Authorization"];
            }

            await blobAxios.put(commitUrl.toString(), blockListXml, {
              headers: {
                "x-ms-version": "2020-10-02",
                "Content-Type": "application/xml",
                "x-ms-blob-content-type": file.type || "application/octet-stream",
              },
            });

            try {
              await this.props.updateUploadedFiles(projectId, fileStatus, sasFile.fileName, result.chainId);
              await this.props.fetchAllProjectFiles(Number(projectId));
            } catch (err) { }

            this.updateProgress(file.name, 100);
          }
        }

        // Restore original published value after upload completes

        if (this.props.updateProject && this.props.project) {
          const currentProject = this.props.project;

          // Determine the final published value
          let finalPublished;
          if (originalPublished === 0 || originalPublished === false || !originalPublished) {
            finalPublished = 0;
          } else if (originalPublished === 1 || originalPublished === true) {
            finalPublished = 1;
          } else {
            finalPublished = originalPublished;
          }

          this.props.updateProject(String(projectId), {
            published: finalPublished,
            // Preserve all existing fields
            chain: currentProject.chain?.id,
            name: currentProject.name,
            season_id: currentProject.season_id,
            year: currentProject.project_year,
            projectType: currentProject.project_type === "Validation" ? 1 : currentProject.project_type === "File Sharing" ? 2 : undefined,
            validation_deadline: currentProject.validation_deadline,
            commentType: currentProject.commentTypes?.map((ct: any) => ct.id),
            commentsAllowed: currentProject.commentsAllowed,
            timezone_id: currentProject.timezone_id,
            est_complete_date: currentProject.est_complete_date,
            automatedDataProcessing:currentProject.automatedDataProcessing,
            expires_at: currentProject.expires_at,
            automatedAnnouncement: currentProject.automatedAnnouncement,
            surveyLink: currentProject.survey_url,
            surveyTitle: currentProject.surveyTitle,
          }).catch((err) => {
            console.error('Error updating project after upload:', err);
          });
        }
        this.props.setLoading(false);
        if (this.props.afterUploadFunction) {
          try {
            await this.props.afterUploadFunction();
          } catch (err) { }
        }
      })
      .then(() => {
        if (projectId) this.props.fetchProject(projectId);
        this.props.setUploadProject(this.props.project);
      })
      .catch(() => {
        this.props.setLoading(false);
        this.props.setError(true);
      });
  }

  onDrop = (files: File[]) => {
    const { prefix, projectId, fileStatus, chain, user } = this.props;
    const fileNames = files.map((f) => f.name);
    const storedUser = getItem(IMPERSONATIONUSER);
    let value
    if (storedUser) {
       value = JSON.parse(storedUser)
    }
    const overRideUser = value || user
    const userId = overRideUser?.id;

    if (this.props.beforeUploadFunction) {
      this.props
        .beforeUploadFunction(prefix, projectId, fileStatus, files)
        .then((result) => {
          if (result) this.handleUpload(prefix, files, chain, projectId, fileStatus, fileNames, userId);
        })
        .catch(() => { });
    } else {
      this.handleUpload(prefix, files, chain, projectId, fileStatus, fileNames, userId);
    }
  };

  onBasicUpload(files: FileList | null) {
    if (!files) return;
    this.onDrop(Array.from(files));
  }

  renderDropzone() {
    const { label, upload: { hasError, loading }, disabled, uploadingState } = this.props;

    // Safe defaults
    const { uploadedFiles = [], uploadProgress = {}, project } = uploadingState || {};
    return (
      <Grid item xs={12}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <Typography style={{ fontWeight: 600, fontSize: "12px" }}>{label}</Typography>
        </div>

        <input
          type="file"
          multiple={this.props.multiple}
          accept={this.props.accept || "*/*"}
          disabled={disabled}
          ref={(ref) => (this.fileInputRef = ref)}
          style={{ display: "none" }}
          onChange={(e) => this.onBasicUpload(e.target.files)}
        />

        <div
          onClick={() => !disabled && this.fileInputRef?.click()}
          onDrop={(e) => {
            e.preventDefault();
            if (!disabled) this.onDrop(Array.from(e.dataTransfer.files));
          }}
          onDragOver={(e) => e.preventDefault()}
          style={{
            border: "2px dashed #ccc",
            borderRadius: "14px",
            padding: "55px",
            textAlign: "center",
            background: this.state.success ? "#e9fce9" : hasError ? "#ffecec" : "#fafafa",
            cursor: disabled ? "not-allowed" : "pointer",
            transition: "0.2s ease",
            opacity: disabled ? 0.4 : 1,
          }}
        >
          {loading && (
            <>
              <img className="spin-right" src={loadingSvg} alt="Loading" />
              <Typography style={{ marginTop: 10, fontSize: 12 }}>Uploading...</Typography>
            </>
          )}

          {hasError && (
            <>
              <img src={errorSvg} alt="Error" />
              <Typography style={{ marginTop: 10, color: "red", fontSize: 12 }}>Upload failed. Try again.</Typography>
            </>
          )}

          {this.state.success && (
            <>
              <img src={successSvg} alt="Success" />
              <Typography style={{ marginTop: 10, color: "#2c7a2c", fontSize: 12 }}>Upload successful!</Typography>
            </>
          )}

          {!loading && !hasError && !this.state.success && (
            <>
              <FolderIcon style={{ color: "#bcbcbc", fontSize: 32, marginBottom: 10 }} />
              <Typography variant="body2" style={{ fontSize: 12 }}>
                {disabled ? ( <span style={{ color: "#666" }}>You must save the project details before loading Project Files</span> ) : ( <>
                <span style={{ color: "#E3BD21" }}>Click to upload</span> or drag & drop
                </> )}
              </Typography>
            </>
          )}
        </div>

        {uploadedFiles.length > 0 && (
          <div
            style={{
              marginTop: 15,
              padding: "15px",
              borderRadius: "12px",
              background: "#ffffff",
              boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography style={{ fontSize: 12, marginBottom: 8, fontWeight: 600, color: "#444" }}>
              Uploading Project Files
            </Typography>
            <ul
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                listStyle: "none",
                padding: 0,
                margin: 0,
              }}
            >
              {uploadedFiles.map((file, index) => {
                const percent = uploadProgress[file] || 0;
                const isComplete = percent === 100;

                return (
                  <li
                    key={index}
                    style={{
                      position: "relative", // for absolute positioning of cross
                      flex: "0 0 calc(33.33% - 8px)", // 3 items per row
                      padding: "8px 12px",
                      background: "#f9f9f9",
                      borderRadius: 8,
                      border: "1px solid #eee",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Cross button at top-right if upload complete */}
                    {isComplete && (
                      <span
                        onClick={() => this.props.removeFile(file)}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          cursor: "pointer",
                          fontWeight: "bold",
                          color: "#ff4d4f",
                          fontSize: 14,
                        }}
                      >
                        ✕
                      </span>
                    )}

                    {/* File name */}
                    <span
                      style={{
                        fontSize: 12,
                        marginBottom: 4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      📎 {file}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Grid>
    );
  }

  renderBasicImport() {
    return this.renderDropzone();
  }

  render() {
    return this.props.basicImport ? this.renderBasicImport() : this.renderDropzone();
  }
}

// Map Redux state
const mapStateToProps = (state: State, ownProps: FileUploadOwnProps) => {
  const key = `${ownProps.prefix}_file_upload` as keyof State;
  const slice: any = state[key];

  return {
    upload: slice as FileUploadState,
    uploadingState: state.uploading || {
      uploadedFiles: [],
      uploadProgress: {},
      loading: false,
      hasError: false,
    },
    // project: project,
  };
};

// Map Redux dispatch
const mapDispatchToProps = (dispatch: Dispatch): FileUploadActions => ({
  fetchProject: (id: string | number) => dispatch(fetchProject(id)),
  getSASURLUpload: (prefix, projectId, chain, fileStatus, fileNames, userId) =>
    dispatch(
      // @ts-ignore
      getSASURLUpload(prefix, projectId, chain, fileStatus, fileNames, userId)
    ),
  updateUploadedFiles: (projectId, fileStatus, filename, chainId) =>
  dispatch(updateUploadedFiles(projectId, fileStatus, filename, chainId, "projectFiles")),
  setUploadProgress: (fileName, percent) => dispatch(setUploadProgress({ fileName, percent })),
  setLoading: (loading) => dispatch(setLoading(loading)),
  setError: (hasError) => dispatch(setError(hasError)),
  clearUploads: () => dispatch(clearUploads()),
  removeFile: (fileName) => dispatch(removeFile(fileName)),
  setUploadProject: (project) => dispatch(setUploadProject(project)),
  updateProject: (id, fields) => dispatch(updateProject(String(id), fields)),
  fetchAllProjectFiles:(projectId: any) =>dispatch(fetchAllProjectFiles(projectId))

});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectCommonFileUpload);
